import type {
  AdminChangePasswordPayload,
  AdminLoginPayload,
  AdminLoginResponse,
  AdminUpdateProfilePayload,
  AdminUser,
  BulkLicenseUpdatePayload,
  CreateCustomerPayload,
  CreateCustomerResponse,
  CustomerListResponse,
  DashboardStats,
  LicenseListResponse,
  PortalAccountResponse,
  UpdateCustomerPayload,
  ValidationLogResponse
} from '../types.js';
import { ApiError, getJson, postJson } from '../http.js';

export const adminApi = {
  async login(payload: AdminLoginPayload): Promise<AdminUser> {
    const response = await postJson<AdminLoginResponse>('/auth/admin/login', payload, { ignoreUnauthorized: true });
    if (!response?.success) {
      throw new ApiError(401, 'invalid_credentials', response);
    }
    return response.user;
  },

  logout(): Promise<{ success: boolean }> {
    return postJson('/auth/admin/logout');
  },

  getCurrentUser(): Promise<AdminUser> {
    return getJson('/admin/current-user');
  },

  getDashboardStats(): Promise<DashboardStats> {
    return getJson('/admin/dashboard-stats');
  },

  getCustomers(params: { page?: number; per_page?: number; search?: string } = {}): Promise<CustomerListResponse> {
    return getJson('/admin/customers', { query: params });
  },

  createCustomer(payload: CreateCustomerPayload): Promise<CreateCustomerResponse> {
    return postJson<CreateCustomerResponse>('/admin/create-customer', payload);
  },

  updateCustomer(customerId: string, payload: UpdateCustomerPayload) {
    return postJson(`/admin/customers/${customerId}/update`, payload);
  },

  getCustomerPortalAccount(customerId: string): Promise<PortalAccountResponse> {
    return getJson(`/admin/customers/${customerId}/portal-account`);
  },

  updateCustomerPortalAccount(customerId: string, payload: { password?: string; status?: 'ACTIVE' | 'INACTIVE' }) {
    return postJson(`/admin/customers/${customerId}/portal-account`, payload);
  },

  getLicenses(params: { page?: number; per_page?: number; status?: string; customer_id?: string } = {}): Promise<LicenseListResponse> {
    return getJson('/admin/licenses', { query: params });
  },

  revokeLicense(license_id: string, reason?: string) {
    return postJson('/admin/revoke-license', { license_id, reason });
  },

  bulkUpdateLicenses(payload: BulkLicenseUpdatePayload) {
    return postJson('/admin/licenses/bulk-update', payload);
  },

  getValidationLogs(params: { page?: number; per_page?: number; result?: string; license_id?: string } = {}): Promise<ValidationLogResponse> {
    return getJson('/admin/validation-logs', { query: params });
  },

  updateProfile(payload: AdminUpdateProfilePayload) {
    return postJson('/profile/admin/update-profile', payload);
  },

  changePassword(payload: AdminChangePasswordPayload) {
    return postJson('/profile/admin/change-password', payload);
  }
};


